import { Button, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { adminUpdateCompanyMall } from "../../../api";
import { colors } from "../../../styles";

interface EditMallModalProps {
    isOpen: boolean;
    onClose: () => void;
    companyId: string;
    companyName: string;
    mall?: string;
}

interface IEditMallForm {
    mall: string;
}

export default function EditMallModal(props: EditMallModalProps) {

    const queryClient = useQueryClient();
    const toast = useToast();

    const { register, handleSubmit, reset, watch, formState: { isValid } } = useForm<IEditMallForm>({
        mode: "onChange",
        defaultValues: {
            mall: props.mall ?? "",
        },
    });

    useEffect(() => {
        if (props.isOpen) {
            reset({ mall: props.mall ?? "" });
        }
    }, [props.isOpen, props.mall, reset]);

    const currentValue = watch("mall") ?? "";
    const originalValue = props.mall ?? "";
    const isChanged = currentValue.trim() !== originalValue.trim();

    const updateMallMutation = useMutation({
        mutationFn: adminUpdateCompanyMall,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetCompanies'] });
            props.onClose();
            toast({
                title: "쇼핑몰을 저장했습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "쇼핑몰 저장에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const onSubmit = (data: IEditMallForm) => {
        updateMallMutation.mutate({
            companyId: props.companyId,
            mall: data.mall.trim(),
        });
    };

    return (
        <Modal isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={20} fontWeight={700} color={colors.black}>
                        {props.companyName || "-"}
                    </Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody>
                    <Stack gap={3}>
                        <Text fontSize={14} fontWeight={400} color={colors.gray}>
                            쇼핑몰
                        </Text>
                        <Input
                            {...register("mall", { required: true, minLength: 1 })}
                            type="text"
                            placeholder="쇼핑몰 URL을 입력해주세요."
                        />
                    </Stack>
                </ModalBody>
                <Stack marginTop={5} width={"100%"} padding={3}>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        isLoading={updateMallMutation.isPending}
                        isDisabled={!isValid || !isChanged}
                        width={"100%"}
                        backgroundColor={colors.blue}
                        color={colors.white}
                        borderRadius={10}
                        _hover={{ bg: colors.blue }}
                        boxShadow="sm"
                    >
                        {originalValue ? "수정" : "등록"}
                    </Button>
                </Stack>
            </ModalContent>
        </Modal>
    );
}
